import React from 'react'
import { cm } from '~/utils/utils'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'accentSecondary'
  | 'outlined'
  | 'borderless'
  | 'destructive'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  isDisabled?: boolean
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-tokens-neutral-bg-action text-tokens-neutral-text-white border-none relative isolate hover:after:bg-tokens-neutral-fill-lighten-8 active:after:bg-tokens-neutral-fill-lighten-12 after:content-[''] after:absolute after:inset-0 after:-z-10",
  secondary:
    'bg-tokens-neutral-bg-white text-tokens-neutral-bg-action border border-tokens-neutral-border-1 shadow-none hover:bg-tokens-neutral-fill-fill-6 active:bg-tokens-neutral-fill-fill-4',
  accentSecondary:
    'bg-tokens-neutral-bg-white text-tokens-status-link-2 border border-tokens-status-link-2/20 shadow-segment hover:shadow-button active:bg-tokens-status-link-base',
  outlined:
    'bg-transparent text-tokens-neutral-text-primary border border-tokens-neutral-border-1 hover:bg-tokens-neutral-fill-fill-6 active:bg-tokens-neutral-fill-fill-4',
  borderless:
    'bg-transparent text-tokens-neutral-text-primary border-none hover:bg-tokens-neutral-fill-fill-6 active:bg-tokens-neutral-fill-fill-4',
  destructive:
    'bg-transparent text-tokens-status-error-text border border-tokens-status-error-1 hover:bg-tokens-status-error-10 active:bg-tokens-status-error-30'
}

const disabledClasses: Record<ButtonVariant, string> = {
  primary: 'bg-tokens-neutral-bg-disabled-2 text-tokens-neutral-text-disabled cursor-not-allowed',
  secondary:
    'bg-tokens-neutral-bg-white border border-tokens-neutral-border-disabled text-tokens-neutral-text-disabled cursor-not-allowed shadow-none',
  accentSecondary:
    'bg-tokens-neutral-bg-white border border-tokens-neutral-border-disabled text-tokens-neutral-text-disabled cursor-not-allowed shadow-none',
  outlined:
    'bg-transparent border border-tokens-neutral-border-disabled text-tokens-neutral-text-disabled cursor-not-allowed',
  borderless: 'text-tokens-neutral-text-disabled cursor-not-allowed bg-transparent',
  destructive:
    'bg-transparent border border-tokens-status-error-30 text-tokens-status-error-30 cursor-not-allowed'
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      className,
      isDisabled,
      isLoading = false,
      leftIcon,
      rightIcon,
      disabled: disabledProp,
      ...props
    },
    ref
  ) => {
    const isButtonDisabled = Boolean(isDisabled || isLoading || disabledProp)
    const iconOnlyClasses = !children && (leftIcon || rightIcon) ? 'px-0 justify-center' : ''

    return (
      <button
        ref={ref}
        className={cm(
          'inline-flex items-center justify-center transition-all duration-200 font-sans font-medium gap-2 whitespace-nowrap rounded-[12px] shadow-tokens-shadow-primary h-9 px-4 py-2 text-base select-none leading-[22px]',
          isButtonDisabled ? disabledClasses[variant] : variantClasses[variant],
          iconOnlyClasses,
          className
        )}
        {...props}
        aria-busy={isLoading || undefined}
        disabled={isButtonDisabled}
      >
        {isLoading ? (
          <span
            aria-hidden='true'
            className='h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent'
          />
        ) : (
          leftIcon && <span className='flex items-center justify-center w-5 h-5'>{leftIcon}</span>
        )}
        {children}
        {rightIcon && <span className='flex items-center justify-center w-5 h-5'>{rightIcon}</span>}
      </button>
    )
  }
)

Button.displayName = 'Button'
